'use client';

import { useState } from 'react';
import PageHeader from "@/components/PageHeader";
import PageFooter from "@/components/PageFooter";
import ContactButton from "@/components/ContactButton";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  return (
    <div className="min-h-screen font-sans">
      <PageHeader />
      <main id="main-content">
      {/* Hero Section */}
      <section className="bg-brand-red text-white py-16 lg:py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
            Contact Us
          </h1>
          <p className="text-xl max-w-3xl mx-auto leading-relaxed">
            Get in touch with our team today. We're here to answer your questions and
            help you find the perfect solution for your business.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8 text-brand-red">Get In Touch</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-brand-red rounded-lg p-3">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-1">Phone</h3>
                    <a href="tel:17187488888" className="text-brand-red text-lg hover:underline">
                      (718) 748-8888
                    </a>
                    <p className="text-gray-600 mt-1">Call us anytime, 24/7</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-red rounded-lg p-3">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-1">Address</h3>
                    <p className="text-gray-700">
                      7014 13th Avenue<br />
                      Brooklyn, NY 11228
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-red rounded-lg p-3">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-1">Email</h3>
                    <a href="mailto:info@answerone.net" className="text-brand-red hover:underline">
                      info@answerone.net
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-brand-red rounded-lg p-3">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-1">Hours</h3>
                    <p className="text-gray-700">
                      24/7 Service Available<br />
                      Office Hours: Mon-Fri 9am-5pm EST
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-xl mb-3">Emergency Support</h3>
                <p className="text-gray-700 mb-4">
                  Need immediate assistance? Our team is available 24/7 to help with any urgent matters.
                </p>
                <ContactButton variant="cta" />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 p-8 rounded-lg">
              <h2 className="text-3xl font-bold mb-6 text-brand-red">Send Us a Message</h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg">
                  Thank you for your message! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 text-red-800 rounded-lg">
                  There was an error submitting your message. Please try again or call us directly.
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block font-bold mb-2">Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-brand-red"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block font-bold mb-2">Company</label>
                  <input
                    type="text"
                    id="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-brand-red"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-bold mb-2">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-brand-red"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block font-bold mb-2">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-brand-red"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block font-bold mb-2">Message *</label>
                  <textarea
                    id="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-brand-red resize-none"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-red text-white px-8 py-4 font-bold hover:bg-opacity-90 transition uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-sm text-gray-600 text-center">
                  * Required fields. We'll respond within 24 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-brand-red">
            Visit Our Brooklyn Office
          </h2>
          <div className="max-w-4xl mx-auto rounded-lg overflow-hidden shadow-lg" style={{height: '450px'}}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3028.0326982876586!2d-73.99688032346314!3d40.624881041593394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c244f133f6527b%3A0x497cc04c331f7e23!2s7014%2013th%20Ave%2C%20Brooklyn%2C%20NY%2011228!5e0!3m2!1sen!2sus!4v1732484956432!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{border: 0}}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="AnswerOne Office Location"
            ></iframe>
          </div>
          <p className="text-center mt-4 text-gray-600">
            <MapPin className="inline mr-2" size={18} />
            7014 13th Avenue, Brooklyn, NY 11228
          </p>
        </div>
      </section>
      </main>
      <PageFooter />
    </div>
  );
}
